/**
 * Search Service
 *
 * API client for message search functionality including media history
 */

import { apiClient } from './api.client';

export interface SearchFilters {
  query?: string;
  channelId?: string;
  dmThreadId?: string;
  type?: 'all' | 'media' | 'links' | 'files';
  limit?: number;
  offset?: number;
  sortBy?: 'relevance' | 'date';
}

export interface SearchResult {
  id: string;
  content: string;
  user_id: string;
  channel_id: string | null;
  dm_thread_id: string | null;
  created_at: string;
  is_edited: boolean;
  edited_at: string | null;
  rank?: number;
  users: {
    id: string;
    display_name: string | null;
    first_name: string;
    last_name: string;
    avatar_url: string | null;
  };
  channel?: {
    id: string;
    name: string;
    type: string;
  } | null;
  message_attachments?: Array<{
    id: string;
    file_name: string;
    mime_type: string;
    file_url?: string;
    file_size?: number;
  }>;
  message_links?: Array<{
    id: string;
    url: string;
    title: string | null;
    description?: string | null;
    favicon_url?: string | null;
  }>;
}

export interface SearchResponse {
  success: boolean;
  results: SearchResult[];
  total: number;
  query?: string;
  filters: {
    channelId?: string;
    dmThreadId?: string;
    type: string;
    sortBy: string;
  };
  pagination: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface UserSearchResult {
  id: string;
  display_name: string | null;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url: string | null;
  role: string;
  is_bot: boolean;
}

export interface ChannelSearchResult {
  id: string;
  name: string;
  description: string | null;
  type: string;
}

interface EntitySearchResponse<T> {
  success: boolean;
  total: number;
  results: T[];
}

/**
 * Search users by name/email
 */
export async function searchUsers(query: string): Promise<UserSearchResult[]> {
  const response = await apiClient.get<EntitySearchResponse<UserSearchResult>>(
    `/search/users?q=${encodeURIComponent(query)}`
  );
  return response.data.results;
}

/**
 * Search channels by name/description
 */
export async function searchChannels(query: string): Promise<ChannelSearchResult[]> {
  const response = await apiClient.get<EntitySearchResponse<ChannelSearchResult>>(
    `/search/channels?q=${encodeURIComponent(query)}`
  );
  return response.data.results;
}

/**
 * Search messages with full-text search or media filtering
 */
export async function searchMessages(
  filters: SearchFilters
): Promise<SearchResponse> {
  const params = new URLSearchParams();

  if (filters.query) params.append('q', filters.query);
  if (filters.channelId) params.append('channelId', filters.channelId);
  if (filters.dmThreadId) params.append('dmThreadId', filters.dmThreadId);
  if (filters.type) params.append('type', filters.type);
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.offset) params.append('offset', filters.offset.toString());
  if (filters.sortBy) params.append('sortBy', filters.sortBy);

  const response = await apiClient.get<SearchResponse>(
    `/search/messages?${params.toString()}`
  );

  return response.data;
}

/**
 * Simple text search (ILIKE-based fallback)
 */
export async function searchMessagesSimple(
  searchText: string,
  filters?: Omit<SearchFilters, 'query' | 'type' | 'sortBy'>
): Promise<SearchResponse> {
  const params = new URLSearchParams();

  params.append('q', searchText);
  if (filters?.channelId) params.append('channelId', filters.channelId);
  if (filters?.dmThreadId) params.append('dmThreadId', filters.dmThreadId);
  if (filters?.limit) params.append('limit', filters.limit.toString());
  if (filters?.offset) params.append('offset', filters.offset.toString());

  const response = await apiClient.get<SearchResponse>(
    `/search/messages/simple?${params.toString()}`
  );

  return response.data;
}

/**
 * Get media history for a channel or DM
 * Convenience wrapper for searchMessages with type=media
 */
export async function getMediaHistory(
  scope: { channelId?: string; dmThreadId?: string },
  options?: { limit?: number; offset?: number }
): Promise<SearchResponse> {
  return searchMessages({
    ...scope,
    type: 'media',
    sortBy: 'date',
    limit: options?.limit || 50,
    offset: options?.offset || 0,
  });
}

/**
 * Get links history for a channel or DM
 * Convenience wrapper for searchMessages with type=links
 */
export async function getLinksHistory(
  scope: { channelId?: string; dmThreadId?: string },
  options?: { limit?: number; offset?: number }
): Promise<SearchResponse> {
  return searchMessages({
    ...scope,
    type: 'links',
    sortBy: 'date',
    limit: options?.limit || 50,
    offset: options?.offset || 0,
  });
}

/**
 * Get files history for a channel or DM
 * Convenience wrapper for searchMessages with type=files
 */
export async function getFilesHistory(
  scope: { channelId?: string; dmThreadId?: string },
  options?: { limit?: number; offset?: number }
): Promise<SearchResponse> {
  return searchMessages({
    ...scope,
    type: 'files',
    sortBy: 'date',
    limit: options?.limit || 50,
    offset: options?.offset || 0,
  });
}
